import { useEffect } from "react"
import { useAppState } from "../../context/appStateContext"
import { PurchasProvider, usePurchase } from "../../context/purchaseContext"
import { UltimateAlertsProvider } from "../../context/ultimateAlertsContext"
import FullScreenPage from "../../components/FullScreenPage/FullScreenPage"
import DelayedCloseButton from "../../components/DelayedCloseButton/DelayedCloseButton"
import UltimateSubfeaturesPopups from "../../components/UltimateSubfeaturesPopups/UltimateSubfeaturesPopups"
import FeatureList from "../../components/FeatureList/FeatureList"
import native from "../../native/native"
import { formatCurrency, formatProductPrice } from "../../utils/utils"
import images from "../../utils/images"
import svg from "../../utils/svg"
import useStrings from "../../localization/useStrings"
import styles from "./TabsPaywall.module.scss"

const TabsPaywall = () => {
  return (
    <PurchasProvider>
      <UltimateAlertsProvider>
        <TabsPaywallContent />
      </UltimateAlertsProvider>
    </PurchasProvider>
  )
}

const TabsPaywallContent = () => {
  const { state, actions } = useAppState()
  const { state: purchaseState, actions: purchaseActions } = usePurchase()

  useEffect(() => {
    actions.setIgnoreFooterInset(true)
    actions.setIgnoreHeaderInset(true)
  }, [actions])

  const isUltimate = purchaseState.ultimateSelected

  const handleTabSelect = (ultimate: boolean) => {
    purchaseActions.setUltimateSelected(ultimate)
  }

  const handlePricingSelect = (yearly: boolean) => {
    purchaseActions.setYearlySelected(yearly)
  }

  const handleContinue = () => {
    purchaseActions.purchaseCurrent()
  }

  const handleClose = () => {
    native.postNativeEvent("close")
  }

  const topInset = state.initialData?.originalTopInset ?? 0
  const bottomInset = state.initialData?.originalBottomInset ?? 0

  return (
    <FullScreenPage addInsetPadding={false}>
      <div className={styles.container}>
        <div
          className={styles.closeButtonContainer}
          style={{ top: topInset + 4 }}
        >
          <DelayedCloseButton onClick={handleClose} />
        </div>
        <TabBar
          isUltimate={isUltimate}
          onTabSelect={handleTabSelect}
          topInset={topInset}
        />
        <div
          className={styles.scrollContainer}
          style={{
            paddingTop: topInset + 64,
            paddingBottom: bottomInset + 230,
          }}
        >
          <ScrollableContent isUltimate={isUltimate} />
        </div>
        <BottomPanel
          isUltimate={isUltimate}
          onPricingSelect={handlePricingSelect}
          onContinue={handleContinue}
          bottomInset={bottomInset}
        />
      </div>
      <UltimateSubfeaturesPopups />
    </FullScreenPage>
  )
}

interface TabBarProps {
  isUltimate: boolean
  onTabSelect: (ultimate: boolean) => void
  topInset: number
}

const TabBar = ({ isUltimate, onTabSelect, topInset }: TabBarProps) => {
  const strings = useStrings()

  return (
    <div className={styles.tabBar} style={{ paddingTop: topInset + 30 }}>
      <button
        className={`${styles.tab} ${!isUltimate ? styles.premiumActive : ""}`}
        onClick={() => onTabSelect(false)}
      >
        <span className={styles.tabLabel}>
          {strings.tabsPaywall.premiumTab}
        </span>
        <div className={styles.tabIndicator} />
      </button>
      <button
        className={`${styles.tab} ${isUltimate ? styles.ultimateActive : ""}`}
        onClick={() => onTabSelect(true)}
      >
        <span className={styles.tabLabel}>
          {strings.tabsPaywall.ultimateTab}
        </span>
        <div className={styles.tabIndicator} />
      </button>
    </div>
  )
}

interface ScrollableContentProps {
  isUltimate: boolean
}

const ScrollableContent = ({ isUltimate }: ScrollableContentProps) => {
  const strings = useStrings()

  return (
    <div
      className={`${styles.contentArea} ${
        isUltimate ? styles.ultimateGradient : styles.premiumGradient
      }`}
    >
      <div className={styles.titleContainer}>
        <div className={styles.planIcon}>
          {isUltimate ? (
            <svg.UltimateIcon className={styles.ultimateIconSvg} />
          ) : (
            <svg.PremiumIcon className={styles.premiumIconSvg} />
          )}
        </div>
        <div className={styles.title}>
          {isUltimate ? (
            <>
              {strings.tabsPaywall.ultimateTitleStart}
              <span className={styles.titleHighlightUltimate}>
                {strings.tabsPaywall.ultimateTitleHighlight}
              </span>
              {strings.tabsPaywall.ultimateTitleEnd}
            </>
          ) : (
            <>
              {strings.tabsPaywall.premiumTitle}
              <br />
              <span className={styles.titleHighlight}>
                {strings.tabsPaywall.premiumTitleHighlight}
              </span>
            </>
          )}
        </div>
      </div>
      <div
        className={`${styles.featureListWrapper} ${isUltimate ? styles.ultimateIcons : ""}`}
      >
        <FeatureList />
      </div>
    </div>
  )
}

interface BottomPanelProps {
  isUltimate: boolean
  onPricingSelect: (yearly: boolean) => void
  onContinue: () => void
  bottomInset: number
}

const BottomPanel = ({
  isUltimate,
  onPricingSelect,
  onContinue,
  bottomInset,
}: BottomPanelProps) => {
  const strings = useStrings()
  const { state: purchaseState } = usePurchase()
  const plan = purchaseState.selectedPlan
  const isYearlySelected = purchaseState.selectedProduct.duration === "yearly"
  const paywallImages = images.premium.default

  const yearlyProduct = plan.yearlyProduct
  const monthlyProduct = plan.monthlyProduct

  const yearlyPrice = formatProductPrice(yearlyProduct)
  const monthlyFromYearly = formatCurrency(
    yearlyProduct.price / 12,
    yearlyProduct.priceLocale,
  )
  const monthlyPrice = formatProductPrice(monthlyProduct)

  return (
    <div
      className={styles.bottomPanel}
      style={{ paddingBottom: bottomInset + 34 }}
    >
      <div className={styles.bottomPanelInner}>
        <div className={styles.pricingCards}>
          {/* Yearly Card */}
          <div
            className={`${styles.pricingCard} ${
              isYearlySelected ? styles.selected : styles.unselected
            } ${isUltimate && isYearlySelected ? styles.ultimateSelected : ""}`}
            onClick={() => onPricingSelect(true)}
          >
            {isYearlySelected && (
              <div className={styles.popularBadge}>
                <span className={styles.popularBadgeText}>
                  {strings.popular}
                </span>
              </div>
            )}
            <div className={styles.pricingCardInner}>
              <div className={styles.pricingLeft}>
                <span className={styles.pricingTitle}>
                  {strings.yearlyPlan}
                </span>
                <span className={styles.pricingSubtitle}>
                  {yearlyPrice}/{strings.year}
                </span>
              </div>
              <div className={styles.pricingRight}>
                <span className={styles.pricingAmount}>
                  {monthlyFromYearly}
                  {strings.tabsPaywall.perMonth}
                </span>
                <div
                  className={`${(isYearlySelected ? paywallImages.checkBox : paywallImages.checkBoxUnchecked).class} ${styles.checkbox}`}
                />
              </div>
            </div>
          </div>

          {/* Monthly Card */}
          <div
            className={`${styles.pricingCard} ${
              !isYearlySelected ? styles.selected : styles.unselected
            } ${isUltimate && !isYearlySelected ? styles.ultimateSelected : ""}`}
            onClick={() => onPricingSelect(false)}
          >
            {!isYearlySelected && (
              <div className={styles.popularBadge}>
                <span className={styles.popularBadgeText}>
                  {strings.popular}
                </span>
              </div>
            )}
            <div className={styles.pricingCardInner}>
              <div className={styles.pricingLeft}>
                <span className={styles.pricingTitle}>
                  {strings.monthlyPlan}
                </span>
              </div>
              <div className={styles.pricingRight}>
                <span className={styles.pricingAmount}>
                  {monthlyPrice}
                  {strings.tabsPaywall.perMonth}
                </span>
                <div
                  className={`${(!isYearlySelected ? paywallImages.checkBox : paywallImages.checkBoxUnchecked).class} ${styles.checkbox}`}
                />
              </div>
            </div>
          </div>
        </div>

        <button className={styles.ctaButton} onClick={onContinue}>
          {purchaseState.selectedProduct.hasTrial
            ? strings.startTrial
            : strings.subscribe}
        </button>
      </div>
    </div>
  )
}

export default TabsPaywall
